class AllBlogsList extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.grid = this.querySelector('[data-all-blogs-grid]');
    this.pagination = this.querySelector('[data-all-blogs-pagination]');
    this.catalogTrack = this.querySelector('[data-catalog-track]');
    this.catalogPrevious = this.querySelector('[data-catalog-previous]');
    this.catalogNext = this.querySelector('[data-catalog-next]');
    this.cards = Array.from(this.querySelectorAll('[data-all-blogs-card]'));
    this.pageSize = Number.parseInt(this.dataset.pageSize, 10) || 12;
    this.totalPages = Math.max(1, Math.ceil(this.cards.length / this.pageSize));

    this.initializeCatalogCarousel();

    if (!this.grid || !this.pagination || this.cards.length === 0) return;

    this.cards
      .sort((first, second) => Number(second.dataset.publishedAt) - Number(first.dataset.publishedAt))
      .forEach((card) => this.grid.append(card));

    this.pagination.addEventListener('click', (event) => {
      const button = event.target.closest('[data-blog-page]');
      if (!button) return;
      this.showPage(Number.parseInt(button.dataset.blogPage, 10), true);
    });

    this.handlePopState = () => this.showPage(this.pageFromUrl(), false);
    window.addEventListener('popstate', this.handlePopState);
    this.showPage(this.pageFromUrl(), false);
  }

  disconnectedCallback() {
    if (this.handlePopState) window.removeEventListener('popstate', this.handlePopState);
    if (this.handleCatalogScroll) this.catalogTrack?.removeEventListener('scroll', this.handleCatalogScroll);
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
  }

  initializeCatalogCarousel() {
    if (!this.catalogTrack || !this.catalogPrevious || !this.catalogNext) return;

    const scrollCatalog = (direction) => {
      this.catalogTrack.scrollBy({ left: direction * this.catalogTrack.clientWidth * 0.82, behavior: 'smooth' });
    };

    this.catalogPrevious.addEventListener('click', () => scrollCatalog(-1));
    this.catalogNext.addEventListener('click', () => scrollCatalog(1));
    this.handleCatalogScroll = () => this.updateCatalogControls();
    this.handleResize = () => this.updateCatalogControls();
    this.catalogTrack.addEventListener('scroll', this.handleCatalogScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    this.updateCatalogControls();
  }

  updateCatalogControls() {
    if (!this.catalogTrack || !this.catalogPrevious || !this.catalogNext) return;

    const maximumScroll = this.catalogTrack.scrollWidth - this.catalogTrack.clientWidth;
    this.catalogPrevious.disabled = this.catalogTrack.scrollLeft <= 2;
    this.catalogNext.disabled = this.catalogTrack.scrollLeft >= maximumScroll - 2;
  }

  pageFromUrl() {
    const page = Number.parseInt(new URL(window.location.href).searchParams.get('blog_page'), 10) || 1;
    return Math.min(Math.max(page, 1), this.totalPages);
  }

  showPage(page, updateUrl) {
    const currentPage = Math.min(Math.max(page, 1), this.totalPages);
    const firstIndex = (currentPage - 1) * this.pageSize;
    const lastIndex = firstIndex + this.pageSize;

    this.cards.forEach((card, index) => {
      card.hidden = index < firstIndex || index >= lastIndex;
    });

    this.renderPagination(currentPage);

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (currentPage === 1) url.searchParams.delete('blog_page');
      else url.searchParams.set('blog_page', String(currentPage));
      window.history.pushState({}, '', url);
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  renderPagination(currentPage) {
    if (this.totalPages <= 1) {
      this.pagination.replaceChildren();
      this.pagination.hidden = true;
      return;
    }

    this.pagination.hidden = false;
    const fragment = document.createDocumentFragment();
    fragment.append(this.createButton('Previous', currentPage - 1, currentPage === 1));

    let previousPage = 0;
    this.pageNumbers(currentPage).forEach((page) => {
      if (previousPage && page - previousPage > 1) {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'gingerflow-all-blogs__ellipsis';
        ellipsis.textContent = '…';
        ellipsis.setAttribute('aria-hidden', 'true');
        fragment.append(ellipsis);
      }

      const button = this.createButton(String(page), page, false);
      if (page === currentPage) {
        button.classList.add('is-active');
        button.setAttribute('aria-current', 'page');
      }
      fragment.append(button);
      previousPage = page;
    });

    fragment.append(this.createButton('Next', currentPage + 1, currentPage === this.totalPages));
    this.pagination.replaceChildren(fragment);
  }

  pageNumbers(currentPage) {
    if (this.totalPages <= 7) {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    }

    return [...new Set([1, currentPage - 1, currentPage, currentPage + 1, this.totalPages])]
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((first, second) => first - second);
  }

  createButton(label, page, disabled) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.dataset.blogPage = String(page);
    button.disabled = disabled;
    button.setAttribute('aria-label', label.match(/^\d+$/) ? `Page ${label}` : `${label} page`);
    return button;
  }
}

if (!customElements.get('all-blogs-list')) {
  customElements.define('all-blogs-list', AllBlogsList);
}
